import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { usersApi } from '@/api/users'
import type { User, AddMemberData } from '@/types'

interface AddMemberModalProps {
  open: boolean
  existingMemberIds: number[]
  onSubmit: (data: AddMemberData) => Promise<void>
  onCancel: () => void
}

export function AddMemberModal({
  open,
  existingMemberIds,
  onSubmit,
  onCancel,
}: AddMemberModalProps) {
  const { t } = useTranslation()
  const [students, setStudents] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedStudents, setSelectedStudents] = useState<User[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      loadStudents()
    } else {
      setSelectedStudents([])
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const loadStudents = async () => {
    setLoading(true)
    try {
      const response = await usersApi.getAll()
      const availableStudents = response.data.filter(
        (student) => !existingMemberIds.includes(student.id)
      )
      setStudents(availableStudents)
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || t('groups.errorLoadStudents'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (selectedStudents.length === 0) {
      setError(t('groups.selectStudents'))
      return
    }

    try {
      setSubmitting(true)
      setError(null)
      await onSubmit({ student_ids: selectedStudents.map((u) => u.id) })
      setSelectedStudents([])
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || t('groups.errorAddMembers'))
    } finally {
      setSubmitting(false)
    }
  }

  const getUserLabel = (student: User) => {
    if (student.first_name && student.surname) {
      return `${student.surname} ${student.first_name} (${student.email})`
    }
    return student.email
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t('groups.addMember')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Autocomplete
          multiple
          options={students}
          getOptionLabel={getUserLabel}
          value={selectedStudents}
          onChange={(_, newValue) => setSelectedStudents(newValue)}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('groups.selectStudents')}
              placeholder={t('groups.selectStudentsPlaceholder')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || selectedStudents.length === 0}
        >
          {submitting ? <CircularProgress size={20} /> : t('groups.addSelected')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
